import { inject, Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { RuntimeConfigService } from '../common/runtime-config.service';
// [AS-569 debug] uncomment alongside the commented console traces below.
// import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserNotificationService {
  private readonly config = inject(RuntimeConfigService);
  private readonly zone = inject(NgZone);
  private readonly ApiUrlHub = `${this.config.apiUrlHub()}/user`;
  private connection: signalR.HubConnection;

  constructor() {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.ApiUrlHub, {
        withCredentials: true,
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    // [AS-569 debug] UserHub lifecycle traces — commented out after the
    // single-session logout root cause was confirmed (is-authenticated is now
    // authoritative server-side). Uncomment this block (and the `environment`
    // import) to re-investigate whether the hub drops before receiving
    // ForceLogout. Only enable on dev — never let console traces reach prod.
    // if (!environment.production) {
    //   this.connection.onreconnecting((err) =>
    //     console.warn('[UserHub] reconnecting…', {
    //       state: this.connection.state,
    //       error: err?.message,
    //       timestamp: new Date().toISOString(),
    //     })
    //   );
    //   this.connection.onreconnected((connectionId) =>
    //     console.log('[UserHub] reconnected', {
    //       connectionId,
    //       state: this.connection.state,
    //       timestamp: new Date().toISOString(),
    //     })
    //   );
    //   this.connection.onclose((err) =>
    //     console.warn('[UserHub] connection closed', {
    //       state: this.connection.state,
    //       error: err?.message,
    //       timestamp: new Date().toISOString(),
    //     })
    //   );
    // }
  }

  getHubConnection(): signalR.HubConnection {
    return this.connection;
  }

  /**
   * Register a UserHub handler that always runs inside Angular's NgZone.
   *
   * SignalR delivers hub messages from its own transport callbacks, which are
   * NOT guaranteed to run inside Angular's zone. A handler that navigates or
   * writes signals from outside the zone schedules no change detection, so the
   * UI only reacts on the next DOM event — the "user must click anywhere before
   * the stale session logs out" symptom of single-active-session ForceLogout.
   *
   * Re-entering the zone here makes every consumer correct by construction and
   * mirrors `CrossTabAuthSyncService`'s `zone.run(...)` for BroadcastChannel.
   * Prefer this over `getHubConnection().on(...)` for any handler with a
   * UI/router/signal side effect.
   */
  on(methodName: string, handler: (...args: unknown[]) => void): void {
    this.connection.on(methodName, (...args: unknown[]) => {
      // [AS-569 debug] inbound-event + zone trace — uncomment (with the
      // `environment` import) to re-confirm ForceLogout arrives out-of-zone and
      // is re-zoned below. Dev only.
      // if (!environment.production) {
      //   console.log(`[UserHub] event received → ${methodName}`, {
      //     inAngularZoneOnArrival: NgZone.isInAngularZone(),
      //     connectionState: this.connection.state,
      //     args,
      //     timestamp: new Date().toISOString(),
      //   });
      // }
      this.zone.run(() => handler(...args));
    });
  }

  async connect(): Promise<void> {
    try {
      await this.connection.start();
      // [AS-569 debug] connection-established trace — uncomment with the
      // `environment` import to re-enable. Dev only.
      // if (!environment.production) {
      //   console.log('[UserHub] connected', {
      //     connectionId: this.connection.connectionId,
      //     state: this.connection.state,
      //     hubUrl: this.ApiUrlHub,
      //     timestamp: new Date().toISOString(),
      //   });
      // }
    } catch (error) {
      throw new Error(`Failed to connect to SignalR hub: ${error}`);
    }
  }

  /**
   * Best-effort teardown of the UserHub connection. Called from
   * `MainLayoutComponent.ngOnDestroy` (and therefore on every forced/voluntary
   * logout, which navigates away from the layout). Swallows errors: a
   * connection that never started or is mid-reconnect can throw on `stop()`,
   * and there is nothing actionable to do while signing out.
   */
  async disconnect(): Promise<void> {
    try {
      await this.connection.stop();
    } catch {
      // intentional no-op — see doc comment.
    }
  }
}
